import { useAuthentication } from '@/hooks/useAuthentication';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useManageStaffStore } from '../store/useManageStaffStore';
import * as XLSX from 'xlsx';
import { toast } from '@/components/ui/use-toast';
import { importStaff } from '@/services/global/admin.service';

export type RowData = { surname: string; forename: string; email: string };

type UseImportStaffParams = {
  refetch?: () => Promise<void> | void;
  setOpen: React.Dispatch<boolean>;
  rowsPerPage?: number;
};

export const useImportStaff = ({ refetch, setOpen, rowsPerPage = 10 }: UseImportStaffParams) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const { schoolContext } = useAuthentication();
  const [data, setData] = useState<RowData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const dialogData = useManageStaffStore((state) => state?.page?.staffImportFromSpreadsheetDialog);
  const [currentPage, setCurrentPage] = useState(1);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    const file = event.target.files[0];

    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target) return;
      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });

      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      const jsonData = XLSX.utils.sheet_to_json<{
        surname: string;
        Surname: string;
        forename: string;
        Forename: string;
        email: string;
        Email: string;
      }>(worksheet);

      const filteredData = jsonData.map((row) => ({
        surname: row.surname || row.Surname,
        forename: row.forename || row.Forename,
        email: row.email || row.Email,
      }));

      if (filteredData.some((row) => !row.email)) {
        toast({
          description: 'Some rows are missing email address',
          variant: 'destructive',
        });
        if (fileInput.current) {
          fileInput.current.value = '';
        }
        return;
      }

      setData(filteredData);
      if (fileInput.current) {
        fileInput.current.value = '';
      }
    };
    reader.readAsBinaryString(file);
  };

  const onImportStaff = async (users?: RowData[]) => {
    setLoadingImport(true);
    const { data, error } = await importStaff(schoolContext?.documentId || '', users);

    if (error) {
      toast({
        title: dialogData?.toastStaffImportedTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingImport(false);
      return;
    }

    toast({
      title: dialogData?.toastStaffImportedTitle || '',
      description: `${data.size} ${dialogData?.toastStaffImportedDescription || ''}`,
    });
    if (refetch) {
      await refetch();
    }
    setLoadingImport(false);
  };

  const handleImportStaff = async () => {
    setIsLoading(true);
    await onImportStaff(data);
    setOpen(false);
    setIsLoading(false);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const addRow = useCallback(() => {
    setData((prev) => {
      const newData = [...prev, { surname: '', forename: '', email: '' }];
      const newTotalPages = Math.ceil(newData.length / rowsPerPage);
      setCurrentPage(newTotalPages);
      return newData;
    });
  }, [rowsPerPage]);

  const removeRow = useCallback(
    (index: number) => {
      const globalIndex = (currentPage - 1) * rowsPerPage + index;
      setData((prev) => prev.filter((_, i) => i !== globalIndex));
      const newDataLength = data.length - 1;
      const newTotalPages = Math.ceil(newDataLength / rowsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    },
    [rowsPerPage, currentPage, data],
  );

  const updateRow = useCallback(
    (index: number, field: keyof RowData, value: string) => {
      const globalIndex = (currentPage - 1) * rowsPerPage + index;
      setData((prev) => prev.map((row, i) => (i === globalIndex ? { ...row, [field]: value } : row)));
    },
    [currentPage, rowsPerPage],
  );

  const hasValidData = useMemo(() => data.length > 0 && data.some((row) => row.email.trim() !== ''), [data]);

  const totalPages = useMemo(() => Math.ceil(data.length / rowsPerPage), [data.length, rowsPerPage]);
  const paginatedData = useMemo(
    () => data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage),
    [data, currentPage, rowsPerPage],
  );
  return {
    fileInput,
    totalPages,
    paginatedData,
    hasValidData,
    loadingImport,
    isLoading,
    currentPage,
    handleFileUpload,
    handleImportStaff,
    handlePageChange,
    addRow,
    removeRow,
    updateRow,
  };
};
